import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/repricing";

const money = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
};

const numberText = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : "";
};

const percent = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : "";
};

const compactDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const statusColor = (status) => {
  if (status === "actioned" || status === "success") return "success";
  if (status === "open") return "warning";
  if (status === "blocked" || status === "failed") return "error";
  return "default";
};

const soldColor = (row) => {
  if (!row.sales_after_action?.order_stats_available) return "default";
  return row.measurement?.sold_after_action ? "success" : "warning";
};

export default function RepricingOutcomeLoop() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState("30");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const loadOutcomes = useCallback(async () => {
    setLoading(true);
    setError("");
    setWarning("");
    try {
      const params = new URLSearchParams({
        limit: "250",
        days,
        include_resolved: "1",
      });
      if (status !== "all") params.set("status", status);
      const res = await fetch(`${API_BASE}/outcome-loop/history?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.message || "Failed to load repricing outcomes");
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setSummary(data.summary || null);
      if (data.schema_ready === false) {
        setWarning(data.message || "Outcome loop schema is not installed.");
      } else if (Array.isArray(data.warnings) && data.warnings.length) {
        setWarning(data.warnings.join(", "));
      }
    } catch (e) {
      setRows([]);
      setSummary(null);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [days, status]);

  useEffect(() => {
    loadOutcomes();
  }, [loadOutcomes]);

  const columns = useMemo(
    () => [
      {
        field: "action_status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => (
          <Chip size="small" color={statusColor(params.row.action_status)} label={params.row.action_status || "unknown"} />
        ),
      },
      {
        field: "listing",
        headerName: "Listing",
        flex: 1,
        minWidth: 250,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {params.row.listing?.title || params.row.listing?.card_name || "Untitled listing"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.listing?.sku || "No SKU"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "recommendation",
        headerName: "Recommendation",
        minWidth: 170,
        sortable: false,
        renderCell: (params) => (
          <Chip size="small" variant="outlined" label={params.row.recommendation?.key || "repricing"} />
        ),
      },
      {
        field: "pushed_price",
        headerName: "Pushed",
        width: 105,
        renderCell: (params) => money(params.row.price?.pushed_price),
      },
      {
        field: "delta_pct",
        headerName: "Change",
        width: 105,
        renderCell: (params) => percent(params.row.price?.delta_pct),
      },
      {
        field: "units_7d",
        headerName: "7d units",
        width: 95,
        renderCell: (params) => numberText(params.row.sales_after_action?.units_7d),
      },
      {
        field: "revenue_30d",
        headerName: "30d revenue",
        width: 125,
        renderCell: (params) => money(params.row.sales_after_action?.revenue_30d),
      },
      {
        field: "profit_30d",
        headerName: "30d profit",
        width: 115,
        renderCell: (params) => money(params.row.sales_after_action?.profit_30d),
      },
      {
        field: "sold_after_action",
        headerName: "Outcome",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            color={soldColor(params.row)}
            label={params.row.measurement?.sold_after_action ? "Sold" : "Watching"}
            variant={params.row.sales_after_action?.order_stats_available ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "created_at",
        headerName: "Action time",
        minWidth: 175,
        renderCell: (params) => compactDate(params.row.created_at),
      },
      {
        field: "last_price_push_at",
        headerName: "Last push",
        minWidth: 175,
        renderCell: (params) => compactDate(params.row.price?.last_price_push_at),
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Outcome Loop
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Repricing action history with post-push sales and profit measurement.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Window" value={days} onChange={(event) => setDays(event.target.value)} sx={{ width: 120 }}>
            <MenuItem value="7">7 days</MenuItem>
            <MenuItem value="30">30 days</MenuItem>
            <MenuItem value="90">90 days</MenuItem>
          </TextField>
          <TextField select size="small" label="Status" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ width: 135 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="actioned">Actioned</MenuItem>
            <MenuItem value="open">Open</MenuItem>
          </TextField>
          <Button variant="outlined" size="small" onClick={loadOutcomes} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {warning && <Alert severity="warning">{warning}</Alert>}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`${numberText(summary?.action_count)} actions`} />
        <Chip color="success" label={`${numberText(summary?.actioned_count)} actioned`} />
        <Chip color="warning" label={`${numberText(summary?.open_count)} open`} />
        <Chip color="success" label={`${numberText(summary?.sold_after_action_count)} sold after`} />
        <Chip label={`${money(summary?.post_action_revenue_30d ?? 0)} 30d revenue`} />
        <Chip label={`${money(summary?.post_action_profit_30d ?? 0)} 30d profit`} />
        {summary?.avg_price_change_pct !== null && summary?.avg_price_change_pct !== undefined ? (
          <Chip label={`${percent(summary.avg_price_change_pct)} avg change`} />
        ) : null}
      </Stack>

      <Paper variant="outlined" sx={{ height: 620, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.action_outcome_id || `${row.listing_id}-${row.created_at}`}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "created_at", sort: "desc" }] },
          }}
        />
      </Paper>
    </Stack>
  );
}
